// src/views/.../ExportGroupsDialog.js
import React, { useMemo } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import Typography from "@mui/material/Typography";
import Checkbox from "@mui/material/Checkbox";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import SearchIcon from "@mui/icons-material/Search";

const ExportGroupsDialog = ({
  open,
  onClose,
  options,
  selectedGroups,
  onToggleGroup,
  searchValue,
  onSearchChange,
  onExport,
  exporting,
}) => {
  const filteredOptions = useMemo(
    () =>
      options.filter((opt) =>
        opt.name.toLowerCase().includes((searchValue || "").toLowerCase())
      ),
    [options, searchValue]
  );

  const allFilteredSelected = 
    filteredOptions.length > 0 &&
    filteredOptions.every((opt) => selectedGroups.includes(opt.id));

  const someFilteredSelected =
    filteredOptions.length > 0 &&
    filteredOptions.some((opt) => selectedGroups.includes(opt.id));

  const handleToggleSelectAll = () => {
    if (filteredOptions.length === 0) return;
    if (allFilteredSelected) {
      filteredOptions.forEach((opt) => {
        if (selectedGroups.includes(opt.id)) {
          onToggleGroup(opt.id);
        }
      });
    } else {
      filteredOptions.forEach((opt) => {
        if (!selectedGroups.includes(opt.id)) {
          onToggleGroup(opt.id);
        }
      });
    }
  };

  const disabledExport = exporting || selectedGroups.length === 0;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle
        sx={{
          bgcolor: "#0DB0DB",
          color: "#ffffff",
          fontWeight: 600,
        }}
      >
        Select Groups to Export
      </DialogTitle>
      <DialogContent dividers>
        <Box sx={{ mb: 2 }}>
          <TextField
            fullWidth
            size="small"
            placeholder="Search groups..."
            value={searchValue}
            onChange={(e) => onSearchChange(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: "#6b7280" }} />
                </InputAdornment>
              ),
            }}
          />
        </Box>
        <Box sx={{ maxHeight: 360, overflowY: "auto" }}>
          {filteredOptions.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No groups found.
            </Typography>
          ) : (
            filteredOptions.map((opt) => (
              <Box
                key={opt.id}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  py: 0.5,
                }}
              >
                <Checkbox
                  checked={selectedGroups.includes(opt.id)}
                  onChange={() => onToggleGroup(opt.id)}
                />
                <Typography variant="body2">{opt.name}</Typography>
              </Box>
            ))
          )}
        </Box>
      </DialogContent>
      <DialogActions
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <Checkbox
            size="small"
            checked={allFilteredSelected}
            indeterminate={someFilteredSelected && !allFilteredSelected}
            onChange={handleToggleSelectAll}
          />
          <Typography variant="body2">Select all</Typography>
        </Box>
        <Box>
          <Button onClick={onClose} disabled={exporting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={onExport}
            disabled={disabledExport}
            startIcon={exporting ? <CircularProgress size={18} /> : null}
          >
            Export
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default ExportGroupsDialog;
